import PrimaryInnerGlowButton from '@/components/buttons/primary';
import CapabilityBlocks from '@/components/CapabilityBlocks';
import Image from '@/components/Image';
import { Container, MAX_CONTAINER_WIDTH } from '@/components/layout/Container';
import Link from '@/components/Link';
import { PRIMARY_NAV_HEIGHT } from '@/components/navigation/PrimaryNavigation';
import HeroIllustration from '@/components/pages/HeroIllustration';
import { CUSTOMER_COUNT } from '@/constants/strings';
import { GlobalContext } from '@/context';
import { TeamLegacyEntryFragmentFragment, TeamQuery } from '@/graphql';
import { getContentType, getSingleLink } from '@/lib/craft-utils';
import gtm from '@/lib/gtm';
import useBreakpoint from '@/lib/hooks/useBreakpoint';
import { parseHeading, parseHtml } from '@/lib/parse-html';
import { maybeNotSet, notEmpty } from '@/lib/typescript-helpers';
import { addElementIdentifier } from '@/lib/utils';
import { COLORS } from '@/styles/color';
import { hexToRGBA } from '@/styles/color/utils';
import { blackScrollbars } from '@/styles/components/scrollbars';
import {
  applyBreakpointNegativeMargins,
  applyBreakpointPadding,
  marginX,
  mediaBreakpointDown,
  mediaBreakpointUp,
  paddingX,
  paddingY,
} from '@/styles/layout/utils';
import { FONT_WEIGHTS } from '@/styles/typography';
import { customTextSize, remCalc } from '@/styles/typography/utils';
import { useContext, useEffect, useMemo, useState } from 'react';
import striptags from 'striptags';
import styled, { css } from 'styled-components';

// #region - Components

const PageWrapper = styled.div`
  overflow: hidden;
  min-height: 100vh;
  padding-top: ${remCalc(PRIMARY_NAV_HEIGHT)};
`;

const HeroHeadline = styled.h1`
  display: inline-block;
  font-weight: ${FONT_WEIGHTS.SEMIBOLD};
  ${customTextSize(38, 44)}
  letter-spacing: -0.01em;

  background-image: linear-gradient(95.06deg, #006eb2 6.22%, #00d47d 86.83%);
  background-clip: text;
  -webkit-background-clip: text;
  color: transparent;

  margin-bottom: ${remCalc(16)};

  ${mediaBreakpointUp('desktopWide')} {
    margin-bottom: ${remCalc(30)};
    ${customTextSize(80, 82)}

    br {
      display: none;
    }
  }
`;

const Paragraph = styled.div`
  ${customTextSize(18, 25)}

  ${mediaBreakpointUp('desktopWide')} {
    ${customTextSize(24, 35)}
  }
`;

const HeroOrb = styled.div`
  position: absolute;
  width: ${remCalc(430)};
  height: ${remCalc(430)};
  top: ${remCalc(29)};
  left: ${remCalc(-289)};
  z-index: -1;

  img {
    width: 100%;
  }

  ${mediaBreakpointDown('mobile')} {
    display: none;
  }

  ${mediaBreakpointUp('desktopWide')} {
    top: ${remCalc(180)};
    left: ${remCalc(-233)};
    width: ${remCalc(540)};
    height: ${remCalc(540)};
  }
`;

const HeroOrbMobile = styled.div`
  position: absolute;
  width: ${remCalc(290)};
  height: ${remCalc(290)};
  top: calc(100% - ${remCalc(20)});
  z-index: -1;
  right: ${remCalc(-230)};

  img {
    width: 100%;
  }

  ${mediaBreakpointUp('tablet')} {
    display: none;
  }
`;

const Hero = styled.header`
  position: relative;
  display: flex;
  flex-direction: column;

  padding-top: ${remCalc(40)};
  margin-bottom: ${remCalc(80)};

  & > div:not(${HeroOrbMobile}) {
    position: relative;
  }

  @media (min-width: ${remCalc(768)}) and (max-width: ${remCalc(1439)}) {
    min-height: ${remCalc(340)};
  }

  ${mediaBreakpointUp('tablet')} {
    position: static;
    flex-direction: row;
    column-gap: 8px;
    padding-top: ${remCalc(64)};
    margin-bottom: ${remCalc(61)};

    > div:first-child {
      padding-left: min(calc(((${remCalc(1296)} - 100vw) / 2) + ${remCalc(130)}), ${remCalc(120)});
      flex: 1;
    }

    > div:nth-child(2) {
      flex: 0 0 ${remCalc(244)};
    }
  }

  ${mediaBreakpointUp('desktopWide')} {
    padding-top: ${remCalc(152)};
    margin-bottom: ${remCalc(89)};

    > div:first-child {
      padding-left: min(calc(((${remCalc(1440)} - 100vw) / 2) + ${remCalc(240)}), ${remCalc(240)});
    }

    > div:nth-child(2) {
      flex: 0 0 ${remCalc(400)};
    }
  }
`;

const CTAWrapper = styled.div`
  text-align: center;
  margin-bottom: ${remCalc(60)};

  ${mediaBreakpointUp('tablet')} {
    margin-bottom: ${remCalc(89)};
  }

  ${mediaBreakpointUp('desktopWide')} {
    margin-bottom: ${remCalc(120)};
  }
`;

const LogoFarm = styled.div`
  margin-bottom: ${remCalc(60)};

  h2 {
    font-weight: ${FONT_WEIGHTS.SEMIBOLD};
    ${customTextSize(18, 22)}
    text-align: center;
    margin-bottom: ${remCalc(16)};
  }

  ${mediaBreakpointUp('tablet')} {
    margin-bottom: ${remCalc(100)};
  }

  ${mediaBreakpointUp('desktopWide')} {
    margin-bottom: ${remCalc(200)};

    h2 {
      ${customTextSize(24, 29)}
      margin-bottom: ${remCalc(24)};
    }
  }
`;

const LogoFarmList = styled.div`
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
  align-items: flex-end;
  justify-content: center;
  column-gap: ${remCalc(40)};
  row-gap: ${remCalc(16)};

  ${mediaBreakpointUp('desktopWide')} {
    column-gap: ${remCalc(64)};
  }
`;

const LogoFarmLogo = styled.div<{ isTaller: boolean }>`
  position: relative;

  img {
    height: ${(props) => (props.isTaller ? 45 * (120 / 90) : 45)}px;
  }

  ${mediaBreakpointUp('desktopWide')} {
    img {
      height: ${(props) => (props.isTaller ? 72 * (120 / 90) : 72)}px;
    }
  }
`;

const ReviewsSection = styled.section`
  background-image: url('/assets/pages/teams/teams-review-background.jpg');
  background-size: cover;
  background-position: center;
  ${paddingY(60)}

  ${applyBreakpointNegativeMargins}

  h2 {
    font-weight: ${FONT_WEIGHTS.SEMIBOLD};
    ${customTextSize(24, 29)}
    text-align: center;
    margin-bottom: ${remCalc(40)};
  }

  ${PrimaryInnerGlowButton} {
    display: none;
  }

  @media (min-width: ${remCalc(MAX_CONTAINER_WIDTH)}) {
    ${marginX(-32)}
    ${paddingX(32)}
    border-radius: ${remCalc(10)};
  }

  ${mediaBreakpointUp('desktopWide')} {
    ${paddingY(120)};

    h2 {
      ${customTextSize(32, 38)}
      margin-bottom: ${remCalc(60)};
    }

    ${PrimaryInnerGlowButton} {
      display: block;
      margin: 0 auto;
    }
  }
`;

const ReviewCard = styled.div`
  display: flex;
  align-items: flex-start;
  flex-direction: column;
  flex: 0 0 ${remCalc(260)};
  scroll-snap-align: center;
  scroll-snap-stop: always;
  background: ${hexToRGBA(COLORS.WHITE, 0.44)};
  border-radius: ${remCalc(10)};
  padding: ${remCalc(50, 33)};

  ${mediaBreakpointUp('desktopWide')} {
    flex-basis: ${remCalc(320)};
    padding: ${remCalc(58, 38)};
  }
`;

const ReviewCardContainer = styled.div`
  display: flex;
  flex-direction: row;

  overflow: auto;
  -webkit-overflow-scrolling: touch;
  scroll-snap-type: x mandatory;

  ${applyBreakpointPadding};
  padding-bottom: ${remCalc(16)};
  column-gap: ${remCalc(16)};

  ${ReviewCard}:first-child {
    margin-left: auto;
  }

  ${ReviewCard}:last-child {
    margin-right: auto;
  }

  ${mediaBreakpointUp('tablet')} {
    column-gap: ${remCalc(28)};
  }
`;

const ReviewCardQuote = styled.div`
  p {
    position: relative;
    ${customTextSize(15, 21)}
    letter-spacing: -0.01em;
    text-align: center;

    ${mediaBreakpointUp('desktopWide')} {
      ${customTextSize(18, 25)}
    }

    strong {
      font-weight: ${FONT_WEIGHTS.SEMIBOLD};
    }

    &::before {
      content: '“';
    }

    &::after {
      content: '”';
    }
  }
`;

const ReviewCardAuthor = styled.div<{ isTaller: boolean }>`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  margin: 0 auto ${remCalc(16)};

  img {
    height: ${(props) => (props.isTaller ? 35 * (120 / 90) : 35)}px;
  }
`;

const ReviewsSectionSwipeText = styled.p`
  font-weight: ${FONT_WEIGHTS.SEMIBOLD};
  ${customTextSize(14, 17)}
  color: ${COLORS.BLUE.LOWLIGHT};
  margin-top: ${remCalc(16)};
  padding: 0;
  text-align: center;

  ${mediaBreakpointUp('tablet')} {
    display: none;
  }
`;

const FeaturesSection = styled.section`
  position: relative;
  padding-top: ${remCalc(80)};

  ${mediaBreakpointUp('tablet')} {
    padding-top: ${remCalc(85)};
  }

  ${mediaBreakpointUp('desktopWide')} {
    padding-top: ${remCalc(120)};
  }
`;

const FeaturesHeadline = styled.h2`
  font-weight: ${FONT_WEIGHTS.SEMIBOLD};
  ${customTextSize(20, 24)}
  text-align: center;
  color: ${COLORS.BLUE.LOWLIGHT};
  margin-left: 32%;
  min-height: ${remCalc(187)};

  ${mediaBreakpointUp('tablet')} {
    width: 47%;
    margin-left: 35%;
    min-height: ${remCalc(162)};
  }

  ${mediaBreakpointUp('desktopWide')} {
    ${customTextSize(49, 54)}
    min-height: ${remCalc(345)};
    width: 57%;
  }
`;

const FeaturesIllustration = styled.div`
  position: absolute;
  top: ${remCalc(-20)};
  left: ${remCalc(-47)};
  width: ${remCalc(168)};

  img {
    width: 100%;
  }

  ${mediaBreakpointUp('tablet')} {
    top: ${remCalc(-40)};
    left: ${remCalc(50)};
  }

  ${mediaBreakpointUp('desktopWide')} {
    width: ${remCalc(324)};
    top: ${remCalc(-87)};
    left: ${remCalc(84)};
  }
`;

const FeatureImage = styled.div`
  position: relative;
  display: block;
  height: 0;
  padding-bottom: ${(685 / 800) * 100}%;
  margin-bottom: ${remCalc(47)};

  video {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
  }
`;

const FeatureList = styled.ul`
  display: flex;
  flex-direction: column;
  row-gap: ${remCalc(32)};
  list-style: none;
  padding: 0;
  margin: 0;
`;

const FeatureGrid = styled.div`
  display: grid;
  grid-template-columns: auto;
  margin-top: ${remCalc(43)};
  margin-bottom: ${remCalc(80)};

  ${mediaBreakpointUp('tablet')} {
    grid-template-columns: 5fr 7fr;
    column-gap: ${remCalc(20)};

    ${FeatureImage} {
      grid-row: 1;
      grid-column: 2;
    }

    ${FeatureList} {
      grid-row: 1;
      grid-column: 1;
      margin-top: ${remCalc(72)};
    }
  }

  ${mediaBreakpointUp('desktopWide')} {
    grid-template-columns: 5fr 7fr;
    margin-top: ${remCalc(-38)};

    ${FeatureList} {
      margin-top: ${remCalc(200)};
    }
  }
`;

const FeatureListItemContent = styled.div`
  display: none;

  p {
    ${customTextSize(13, 18)}
    margin-bottom: ${remCalc(18)};
  }

  ${mediaBreakpointUp('desktopWide')} {
    p {
      ${customTextSize(18, 25)}
    }
  }
`;

const FeatureListItem = styled.li<{ isActive: boolean }>`
  border-left: ${remCalc(4)} solid transparent;
  padding-left: ${remCalc(12)};

  button {
    appearance: none;
    outline: none;
    border: none;
    font-weight: ${FONT_WEIGHTS.SEMIBOLD};
    ${customTextSize(18, 22)}
    margin-bottom: ${remCalc(10)};
    padding: 0;
    background: transparent;
    cursor: pointer;
    text-align: left;
  }

  ${(props) =>
    props.isActive &&
    css`
      border-color: ${COLORS.BLUE.LOWLIGHT};

      ${FeatureListItemContent} {
        display: block;
      }
    `}

  ${mediaBreakpointUp('desktopWide')} {
    padding-left: ${remCalc(18)};

    button {
      ${customTextSize(24, 29)}
    }
  }
`;

const FeatureLearnMoreLink = styled.a`
  display: inline-flex;
  align-items: center;
  font-weight: ${FONT_WEIGHTS.SEMIBOLD};
  ${customTextSize(12, 14)}
  margin: 0;
  color: ${COLORS.BLACK};
  text-decoration: none;

  &::after {
    content: '';
    display: inline-block;
    width: ${remCalc(10)};
    height: ${remCalc(10)};
    margin-left: ${remCalc(8)};
    background-image: url("data:image/svg+xml,%3Csvg width='12' height='13' viewBox='0 0 12 13' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M11.5653 5.05437C12.1449 5.72969 12.1449 6.8206 11.5653 7.51324L7.46403 12.2751L6.30496 10.9245L9.33637 7.37471H0V5.1929H9.35123L6.2901 1.64311L7.44917 0.275146L11.5653 5.05437Z' fill='%23001B38'/%3E%3C/svg%3E");
    background-size: contain;
  }

  ${mediaBreakpointUp('desktopWide')} {
    ${customTextSize(16, 26)}
  }
`;

const IntegrationsSection = styled.section`
  position: relative;
  padding-bottom: ${remCalc(158)};
  text-align: center;

  h2 {
    font-weight: ${FONT_WEIGHTS.SEMIBOLD};
    ${customTextSize(24, 29)}
    color: ${COLORS.GREEN.LOWLIGHT};
    margin: 0 auto ${remCalc(14)};

    & + p {
      ${customTextSize(18, 25)}
      margin: 0 auto;
    }
  }

  ${mediaBreakpointUp('tablet')} {
    h2,
    h2 + p {
      width: 75%;
      margin: 0 auto;
    }

    h2 {
      margin-bottom: ${remCalc(20)};
    }
  }

  ${mediaBreakpointUp('desktopWide')} {
    h2,
    h2 + p {
      width: 53%;
      margin: 0 0 0 21%;
    }

    h2 {
      ${customTextSize(49, 59)}
      margin-bottom: ${remCalc(20)};

      & + p {
        ${customTextSize(24, 34)}
      }
    }
  }
`;

const IntegrationImageWrapper = styled.div`
  p {
    ${customTextSize(18, 24)}
    text-align: center;
    width: ${remCalc(226)};
    margin: ${remCalc(70)} auto 0;
  }

  ${mediaBreakpointUp('tablet')} {
    p {
      margin-top: 0;
    }
  }
`;

const IntegrationImage = styled.div`
  width: 100%;
  max-width: ${remCalc(268)};
  display: block;
  margin: ${remCalc(63)} auto 0;

  img {
    width: 100%;
  }

  ${mediaBreakpointUp('tablet')} {
    max-width: ${remCalc(595)};
    margin-top: ${remCalc(82)};
  }

  ${mediaBreakpointUp('desktopWide')} {
    max-width: ${remCalc(700)};
    margin-top: ${remCalc(120)};
  }
`;

const IntegrationsSectionOrb = styled.img`
  position: absolute;
  right: ${remCalc(-192)};
  top: ${remCalc(-189)};
  z-index: -1;
  display: none;
  width: ${remCalc(500)};
  height: ${remCalc(500)};

  ${mediaBreakpointUp('desktopWide')} {
    display: block;
  }
`;

const ResourcesSection = styled.section`
  margin: 0 auto ${remCalc(60)};
  min-height: ${remCalc(100)};
  background-color: ${COLORS.GRAY.HIGHLIGHT};
  background-image: url('/assets/pages/teams/teams-resource-background.jpg');
  background-size: cover;
  background-position: 80% center;
  padding: ${remCalc(60)} 0;

  ${applyBreakpointNegativeMargins}

  ${mediaBreakpointUp('tablet')} {
    border-radius: ${remCalc(10)};
    padding: ${remCalc(120)} 0;
  }

  @media (min-width: ${remCalc(MAX_CONTAINER_WIDTH)}) {
    ${marginX(-60)}
    ${paddingX(60)}
    border-radius: ${remCalc(10)};
  }
`;

const ResourcesSectionHeadline = styled.h3`
  font-weight: ${FONT_WEIGHTS.SEMIBOLD};
  ${customTextSize(24, 29)}
  text-align: center;
  margin: 0 auto ${remCalc(40)};
  padding: 0;
  display: block;
  max-width: ${remCalc(480)};

  ${mediaBreakpointUp('desktopWide')} {
    ${customTextSize(32, 38)}
    max-width: ${remCalc(800)};
    margin-bottom: ${remCalc(60)};
  }
`;

const ResourcesCard = styled.div`
  display: flex;
  align-items: flex-start;
  flex-direction: column;
  flex: 0 0 ${remCalc(260)};
  scroll-snap-align: center;
  scroll-snap-stop: always;
  background: ${hexToRGBA(COLORS.WHITE, 0.8)};
  border-radius: ${remCalc(10)};
  padding: ${remCalc(22, 25, 31)};

  a {
    color: inherit;
    text-decoration: none;
  }

  @media (min-width: ${remCalc(MAX_CONTAINER_WIDTH)}) {
    flex: 1 1 ${remCalc(284)};
  }

  ${mediaBreakpointUp('desktopWide')} {
    padding: ${remCalc(26, 28, 28)};
  }
`;

const ResourcesCardContainer = styled.div`
  display: flex;
  column-gap: ${remCalc(16)};

  overflow: auto;
  -webkit-overflow-scrolling: touch;
  scroll-snap-type: x mandatory;

  margin-bottom: ${remCalc(28)};
  padding: ${remCalc(0, 32, 16)};

  ${mediaBreakpointUp('tablet')} {
    margin-bottom: ${remCalc(16)};
  }

  @media (min-width: ${remCalc(MAX_CONTAINER_WIDTH)}) {
    ${paddingX(0)}
  }

  ${mediaBreakpointUp('desktopWide')} {
    column-gap: ${remCalc(20)};
  }

  ${blackScrollbars}

  ${ResourcesCard} {
    &:first-child {
      margin-left: auto;
    }

    &:last-child {
      margin-right: auto;
    }
  }
`;

const ResourceCardEyebrow = styled.p`
  font-weight: ${FONT_WEIGHTS.SEMIBOLD};
  ${customTextSize(13, 15)}
  letter-spacing: 0.03em;
  text-transform: uppercase;
  margin: 0 0 ${remCalc(44)} 0;

  ${mediaBreakpointUp('desktopWide')} {
    margin-bottom: ${remCalc(57)};
  }
`;

const ResourceCardTitle = styled.p`
  font-weight: ${FONT_WEIGHTS.SEMIBOLD};
  ${customTextSize(22, 25)}
  margin: 0 0 ${remCalc(16)} 0;

  ${mediaBreakpointUp('desktopWide')} {
    ${customTextSize(27, 30)}
  }
`;

const ResourceCardSummary = styled.p`
  ${customTextSize(14, 19)}
  margin: 0 0 ${remCalc(35)} 0;

  ${mediaBreakpointUp('desktopWide')} {
    ${customTextSize(16, 22)}
    margin-bottom: ${remCalc(48)};
  }
`;

const ResourcesSectionSwipeText = styled.p`
  font-weight: ${FONT_WEIGHTS.SEMIBOLD};
  ${customTextSize(14, 17)}
  color: ${COLORS.WHITE};
  text-align: center;
  color: ${COLORS.BLUE.LOWLIGHT};

  ${mediaBreakpointUp('tablet')} {
    display: none;
  }
`;

const ReadMoreLink = styled.a`
  display: inline-block;
  width: auto;
  border-bottom: 2px solid;
  font-weight: ${FONT_WEIGHTS.BOLD};
  ${customTextSize(16, 19)}
  margin: auto 0 0 0;
  color: ${COLORS.BLACK};
  text-decoration: none;
`;

// #endregion - Components

const TeamsLegacyLayout: PageTemplate<TeamQuery> = ({ entry }: { entry: TeamLegacyEntryFragmentFragment }) => {
  const { toggleGetStartedModal } = useContext(GlobalContext);
  const [currentBreakpoint] = useBreakpoint();

  const [activeFeatureId, setActiveFeatureId] = useState(entry?.featureSwitcher?.[0]?.id);

  const imageBreakpoint = useMemo(
    () =>
      currentBreakpoint === 'desktopWide'
        ? 'large'
        : currentBreakpoint === 'desktop' || currentBreakpoint === 'tablet'
          ? 'medium'
          : 'small',
    [currentBreakpoint],
  );

  const activeFeature = entry?.featureSwitcher?.find((feature) => feature?.id === activeFeatureId);

  useEffect(() => {
    setActiveFeatureId(entry?.featureSwitcher?.[0]?.id);
  }, [entry]);

  const differentiationCta = getSingleLink(entry.differentiationCta);
  const featureCta = getSingleLink(entry.featureCta);

  return (
    <PageWrapper>
      <Container>
        <Hero>
          <div>
            <HeroHeadline>{parseHeading(entry?.heroText)}</HeroHeadline>
            <Paragraph>{parseHtml(entry?.heroRichText)}</Paragraph>
          </div>

          <div>
            <HeroIllustration name={entry?.teamHeroIllustration} />
          </div>

          <HeroOrbMobile>
            <Image src={`/assets/pages/teams/teams-orb-hero-small.png`} width={291} height={291} alt="" />
          </HeroOrbMobile>
        </Hero>
      </Container>

      <HeroOrb>
        <Image
          key={`orb-${imageBreakpoint}`}
          src={`/assets/pages/teams/teams-orb-hero-${imageBreakpoint}.png`}
          width={291}
          height={291}
          alt=""
        />
      </HeroOrb>

      <Container>
        <CapabilityBlocks
          blocks={
            entry?.differentiationBlocks
              ?.map((block, i) => ({
                icon: {
                  src: block?.icon?.[0]?.url!,
                  width: block?.icon?.[0]?.width!,
                  height: block?.icon?.[0]?.height!,
                },
                supertitle: block?.supertitle ?? undefined,
                title: block?.primaryTitle ?? '',
                text: parseHtml(block?.text),
              }))
              .filter(notEmpty) ?? []
          }
        />

        {differentiationCta ? (
          <CTAWrapper>
            <Link href={differentiationCta.url}>
              <PrimaryInnerGlowButton
                as="a"
                onClick={() => {
                  gtm.track('click', {
                    element_type: 'link',
                    element_label: maybeNotSet(differentiationCta.text),
                    location: 'Differentiation Section',
                  });
                }}
                {...addElementIdentifier('differentiation-cta')}
              >
                {differentiationCta.text}
              </PrimaryInnerGlowButton>
            </Link>
          </CTAWrapper>
        ) : null}

        {!!entry?.socialProofCustomers?.length && (
          <LogoFarm>
            <h2>{entry.socialProofHeadline ?? `${CUSTOMER_COUNT}+ High-impact teams rely on Front`}</h2>

            <LogoFarmList>
              {entry.socialProofCustomers.map((customer) => {
                if (!customer?.logo?.[0]?.url) return null;

                return (
                  <LogoFarmLogo key={customer.id} isTaller={customer.isMonochromeLogoTaller ?? false}>
                    <Image
                      src={customer?.logo?.[0]?.url!}
                      width={customer?.logo?.[0]?.width!}
                      height={customer?.logo?.[0]?.height!}
                      alt=""
                    />
                  </LogoFarmLogo>
                );
              })}
            </LogoFarmList>
          </LogoFarm>
        )}

        {!!entry?.customerTestimonialBlocks?.length && (
          <ReviewsSection>
            <h2>{entry.customerTestimonialHeadline ?? ''}</h2>

            <ReviewCardContainer>
              {entry.customerTestimonialBlocks?.map((review, i) => (
                <ReviewCard key={`review-card-${i}`}>
                  <ReviewCardAuthor isTaller={review?.customer?.[0]?.isMonochromeLogoTaller ?? false}>
                    <Image
                      src={review?.customer?.[0]?.logo?.[0]?.url!}
                      width={review?.customer?.[0]?.logo?.[0]?.width!}
                      height={review?.customer?.[0]?.logo?.[0]?.height!}
                      alt=""
                    />
                  </ReviewCardAuthor>

                  <ReviewCardQuote>{parseHtml(review?.quote)}</ReviewCardQuote>
                </ReviewCard>
              ))}
            </ReviewCardContainer>

            <ReviewsSectionSwipeText>Swipe left to see more testimonials</ReviewsSectionSwipeText>
          </ReviewsSection>
        )}

        <FeaturesSection>
          <FeaturesHeadline>{entry?.featureHeading ?? ''}</FeaturesHeadline>

          <FeaturesIllustration>
            <Image src="/webassets/illustrations/man-walking-with-briefcase.svg" width={168} height={287} alt="" />
          </FeaturesIllustration>

          <FeatureGrid>
            <FeatureImage>
              <video key={activeFeature?.id} autoPlay loop={false} muted playsInline>
                <source src={activeFeature?.featureAsset?.[0]?.url!} />
              </video>
            </FeatureImage>

            <FeatureList>
              {entry?.featureSwitcher?.map((feature) => (
                <FeatureListItem key={feature?.id} isActive={activeFeatureId === feature?.id}>
                  <button
                    onClick={() => {
                      setActiveFeatureId(feature?.id);

                      gtm.track('click', {
                        element_label: striptags(maybeNotSet(feature?.title)),
                        element_type: 'button',
                        location: 'Features',
                      });
                    }}
                  >
                    {parseHtml(feature?.title)}
                  </button>

                  <FeatureListItemContent>{parseHtml(feature?.description)}</FeatureListItemContent>
                </FeatureListItem>
              ))}
            </FeatureList>
          </FeatureGrid>
        </FeaturesSection>

        {featureCta ? (
          <CTAWrapper>
            <Link href={featureCta.url}>
              <PrimaryInnerGlowButton
                as="a"
                onClick={() => {
                  gtm.track('click', {
                    element_label: maybeNotSet(featureCta.text),
                    element_type: 'link',
                    location: 'Features Section',
                  });
                }}
              >
                {featureCta.text}
              </PrimaryInnerGlowButton>
            </Link>
          </CTAWrapper>
        ) : null}

        <IntegrationsSection>
          <h2>{entry?.integrationsHeading ?? ''}</h2>

          {parseHtml(entry?.integrationsText)}

          <IntegrationImageWrapper>
            <IntegrationImage>
              <Image
                key={`image-${imageBreakpoint}`}
                src={`/assets/pages/product-overview/integrations-${imageBreakpoint === 'small' ? 'vertical' : 'horizontal'
                  }.png`}
                width={imageBreakpoint === 'small' ? 536 : 1395}
                height={imageBreakpoint === 'small' ? 1394 : 536}
                alt=""
              />
            </IntegrationImage>

            <p>{entry?.integrationsCaption ?? 'Integrate your favorite apps into Front—or build your own.'}</p>
          </IntegrationImageWrapper>

          <IntegrationsSectionOrb src="/assets/pages/teams/teams-integrations-orb.png" />
        </IntegrationsSection>

        {!!entry?.relatedResources?.length && (
          <ResourcesSection>
            <ResourcesSectionHeadline>
              {entry?.resourcesHeading ?? 'Resources to deliver extraordinary experiences'}
            </ResourcesSectionHeadline>

            <ResourcesCardContainer>
              {entry?.relatedResources?.map((resource) => (
                <ResourcesCard key={resource?.id}>
                  <ResourceCardEyebrow>{getContentType(resource?.__typename ?? '')}</ResourceCardEyebrow>

                  <Link href={resource?.url ?? '#'}>
                    <a>
                      <ResourceCardTitle>{resource?.title}</ResourceCardTitle>
                    </a>
                  </Link>

                  {resource && 'summary' in resource && resource.summary ? (
                    <ResourceCardSummary>{resource.summary}</ResourceCardSummary>
                  ) : null}

                  <Link href={resource?.url ?? '#'}>
                    <ReadMoreLink>Read more</ReadMoreLink>
                  </Link>
                </ResourcesCard>
              ))}
            </ResourcesCardContainer>

            <ResourcesSectionSwipeText>Swipe left to see more resources</ResourcesSectionSwipeText>
          </ResourcesSection>
        )}

        <CTAWrapper>
          <PrimaryInnerGlowButton
            type="button"
            onClick={() => {
              toggleGetStartedModal(true);

              gtm.track('click', {
                element_type: 'button',
                element_label: maybeNotSet(entry?.footerCtaText),
                location: 'Resources',
              });
            }}
          >
            {entry?.footerCtaText ?? 'Get started'}
          </PrimaryInnerGlowButton>
        </CTAWrapper>
      </Container>
    </PageWrapper>
  );
};

export default TeamsLegacyLayout;
